import { useState } from "react";
import { Navigate } from "react-router-dom";
import { AlertCircle, Clock, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { findFirstAccessiblePath } from "@/lib/rbacRoutes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Shown when the user is signed in to Cognito but has no RBAC groups / permissions yet.
 */
export default function PendingAccess() {
  const { user, logout, refreshSession } = useAuth();
  const { hasAnyAccess, loading, error, refreshPermissions, hasPermission } = usePermissions();
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");

  if (!loading && hasAnyAccess) {
    const home = findFirstAccessiblePath(hasPermission) || "/";
    return <Navigate to={home} replace />;
  }

  const handleCheckAgain = async () => {
    setChecking(true);
    setCheckError("");
    try {
      const result = await refreshSession();
      if (!result?.success) {
        setCheckError(
          result?.error
          || "Your session was ended when an administrator changed your role. Sign out and sign in again to load the new access.",
        );
        return;
      }
      await refreshPermissions();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-lg w-full" data-testid="pending-access">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-cp-inset">
            <Clock className="h-6 w-6 text-cp-indigo" />
          </div>
          <CardTitle>Access not assigned yet</CardTitle>
          <CardDescription>
            Your Halo account exists, but an administrator has not assigned you to an
            access group yet. Groups control which pages and actions you can use.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {user?.email ? (
            <p className="text-sm text-center text-muted-foreground">
              Signed in as <span className="font-medium text-foreground">{user.email}</span>
            </p>
          ) : null}
          {error || checkError ? (
            <p className="flex items-start gap-2 rounded-md border border-cp-mod bg-cp-inset px-3 py-2 text-sm text-cp-mod">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {checkError
                  || `Could not load your access from the server (${error}). If an admin just assigned you, sign out and sign in again so a new token is issued.`}
              </span>
            </p>
          ) : null}
          <p className="text-sm text-center text-muted-foreground">
            Ask a platform administrator to invite you to a group such as{" "}
            <span className="font-medium">Ticket Operator</span> or{" "}
            <span className="font-medium">Platform Administrator</span>. After they
            assign you, click <span className="font-medium">Check again</span>, or{" "}
            <span className="font-medium">Sign out</span> and sign back in if Check
            again fails (role changes invalidate the refresh token).
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={checking}
              onClick={handleCheckAgain}
            >
              {checking ? "Checking…" : "Check again"}
            </Button>
            <Button type="button" variant="secondary" onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
